<template>
  <MarketingPageTemplate
    :title="data.title || 'Kampagnenübersicht'"
    :subtitle="data.subtitle || 'Kanalübergreifende Übersicht aller Marketingmaßnahmen nach Projekt, Kampagne und Zeitraum'"
    :loading="loading"
    :loaded-once="loadedOnce"
    :error="error"
    :legend="data.legend || []"
    loading-text="Lade Kampagnenübersicht…"
    @retry="reload"
  >
    <template #kpis>
      <!-- UX: Wie im Finanz-Dashboard nur 4 Leitkennzahlen als Karten. Die übrigen
           Strukturzahlen stehen als Meta-Zeile bzw. in der Kanalliste – keine Kachelwand mit 13 Kästen. -->
      <MarketingKpis :columns="4">
        <KpiTile :card="kpi('campaigns')" :meta="`in ${formatNumber(kpi('projects')?.value)} ${kpi('projects')?.value === 1 ? 'Projekt' : 'Projekten'}`" />
        <KpiTile
          :card="kpi('measures')"
          :meta="`${formatNumber(kpi('running')?.value)} laufend · ${formatNumber(kpi('completed')?.value)} abgeschlossen`"
        />
        <KpiTile :card="kpi('gross_value')" meta="Summe Print-Mediaplan" />
        <KpiTile :card="kpi('net_value')" meta="Summe Print-Mediaplan" />
      </MarketingKpis>
    </template>

    <template #main>
      <!-- UX: Leistung je Kanal als Kennzahl-Liste. Jede Zeile ist
           zugleich der Einstieg in die Detailseite und übernimmt die Filter. -->
      <DashboardSection
        title="Leistung je Kanal"
        :subtitle="`${formatNumber(kpi('channels')?.value)} eingesetzte Kanäle · Details führen zur gefilterten Detailseite`"
      >
        <KpiTable :columns="PERFORMANCE_COLUMNS" :rows="performanceRows" />
      </DashboardSection>
      <!-- Wie bisher: Legende der Schaltungstypen aus den Filteroptionen. -->
      <DashboardSection title="Schaltungstypen" :empty="!(filterOptions.placement_types || []).length">
        <PlacementTypeLegend :items="filterOptions.placement_types || []" />
      </DashboardSection>
    </template>

    <!-- Reihenfolge wie bisher: Matrix, dann Timeline -->
    <ChannelMatrix :matrix="data.matrix || { channels: [], rows: [] }" />
    <MarketingTimeline :items="data.timeline || []" :print-titles="filterOptions.print_titles || []" />
  </MarketingPageTemplate>
</template>

<script setup>
import { computed } from 'vue';
import MarketingPageTemplate from '../components/MarketingPageTemplate.vue';
import DashboardSection from '../components/DashboardSection.vue';
import MarketingKpis from '../components/MarketingKpis.vue';
import KpiTile from '../components/KpiTile.vue';
import KpiTable from '../components/KpiTable.vue';
import ChannelMatrix from '../components/ChannelMatrix.vue';
import MarketingTimeline from '../components/MarketingTimeline.vue';
import PlacementTypeLegend from '../components/PlacementTypeLegend.vue';
import { CHANNEL_ROUTES, formatNumber } from '../composables/formatters';
import { useMarketingDashboard } from '../composables/useMarketingDashboard';
import { useMarketingFilters } from '../composables/useMarketingFilters';

defineProps({
  filterOptions: { type: Object, default: () => ({}) }
});

const { queryFor } = useMarketingFilters();
const { data, loading, error, loadedOnce, reload } = useMarketingDashboard('/mb/overview/');

const PERFORMANCE = [
  { key: 'digital_impressions', channel: 'digital', label: 'Digitale Kampagnen' },
  { key: 'social_impressions', channel: 'social', label: 'Social Media' },
  { key: 'emails_sent', channel: 'email', label: 'E-Mail- und Newsletter-Marketing' },
  { key: 'sessions', channel: 'traffic', label: 'Website-Traffic' },
  { key: 'print_placements', channel: 'print', label: 'Print-Kampagnen' }
];

const PERFORMANCE_COLUMNS = [
  { key: 'name', label: 'Kanal' },
  { key: 'metric', label: 'Kennzahl', text: true },
  { key: 'value', label: 'Wert' },
  { key: 'link', label: '', link: true }
];

const kpis = computed(() => data.value.kpis || []);

function kpi(key) {
  return kpis.value.find((item) => item.key === key) || null;
}

const definitions = computed(() => Object.fromEntries((data.value.legend || []).map((item) => [item.kpi_key, item])));

const performanceRows = computed(() => PERFORMANCE
  .map((item) => {
    const card = kpi(item.key);
    if (!card) return null;
    const route = CHANNEL_ROUTES[item.channel];
    return {
      key: item.key,
      label: item.label,
      info: definitions.value[item.key]?.definition_de || '',
      values: { metric: card.label, value: card },
      to: { name: route, query: queryFor(route) }
    };
  })
  .filter(Boolean));
</script>
